<script>
import Reactions from "@vvg/forum/js/mixins/reactions";
import ReactionItem from "@vvg/forum/vue/reaction/_item.vue";
import ReactionForm from "@vvg/forum/vue/reaction/_form.vue";
import Permissions from '@vvg/forum/js/mixins/permissions.js';

export default {
  name: "reactions",
  components: {ReactionItem, ReactionForm},
  mixins: [Reactions, Permissions],
  props: {
    topicId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      reactions: [],
      showRootForm: false,
      loading: false,
      loadingMore: false,
      initialLimit: 5,
      loadMoreLimit: 5,
      hasMore: false,
      totalAll: 0,
    }
  },
  computed: {
    remainingReactions() {
      return Math.max(0, this.totalAll - this.reactions.length);
    },
    hasReactions() {
      return this.reactions.length > 0;
    }
  },
  mounted() {
    this.getReactions();
  },
  methods: {
    async getReactions() {
      this.loading = true;

      const url = this.buildReactionsUrl({
        topic_id: this.topicId,
        limit: this.initialLimit,
        offset: 0,
      });

      try {
        const response = await Promise.resolve(this.Ajax(url));
        this.reactions = response.data;
        this.hasMore = response.has_more;
        this.totalAll = response.total_all;
      } catch (e) {
        console.error('getReactions mislukt:', e);
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;

      this.loadingMore = true;

      const url = this.buildReactionsUrl({
        topic_id: this.topicId,
        limit: this.loadMoreLimit,
        offset: this.reactions.length,
      });

      try {
        const response = await Promise.resolve(this.Ajax(url));
        this.reactions = this.reactions.concat(response.data);
        this.hasMore = response.has_more;
        this.totalAll = response.total_all;
      } catch (e) {
        console.error('loadMore mislukt:', e);
      } finally {
        this.loadingMore = false;
      }
    },

    onSaved(savedReaction) {
      this.showRootForm = false;
      if (!savedReaction) return;

      this.insertReactionLocally(this.reactions, savedReaction);
      this.totalAll++;
      // hasMore niet aanpassen — de root-lijst groeide met 1,
      // en totalAll groeide met 1, dus de verhouding blijft gelijk
    },

    onReactionChanged(payload) {
      this.handleReactionChange(this.reactions, payload, {
        onDeleted: () => {
          this.totalAll--;
          this.hasMore = this.totalAll > this.reactions.length;
        },
        onChildAdded: () => {
          this.totalAll++;
          // root-lijst groeit niet door een geneste reply,
          // dus hasMore niet aanpassen
        },
      });
    },
  }
}
</script>

<template>
  <div class="reactions">
    <div class="reactions__header">
      <span class="reactions__title">Reacties ({{ totalAll }})</span>
      <button
          v-if="!showRootForm && isAuthenticated()"
          type="button"
          class="btn btn-dark btn-sm d-flex align-items-center gap-2"
          @click="showRootForm = true"
      >
        <i class="fa fa-comment"></i> Reageren
      </button>
    </div>

    <div v-if="showRootForm" class="reactions__root-form">
      <ReactionForm
          :topic-id="topicId"
          :parent-id="null"
          @saved="onSaved"
          @cancel="showRootForm = false"
      />
    </div>

    <div v-if="loading" class="reactions__loading">
      <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
      <span class="text-muted">Reacties laden...</span>
    </div>

    <div v-else-if="!hasReactions && !showRootForm" class="reactions__empty">
      <i class="fa fa-comments reactions__empty-icon"></i>
      <p class="mb-1">Nog geen reacties.</p>
      <span v-if="isAuthenticated()" class="small text-muted">Wees de eerste en deel je mening!</span>
    </div>

    <ul v-else class="reactions__list list-unstyled mb-0">
      <ReactionItem
          v-for="reaction in reactions"
          :key="reaction.id"
          :reaction="reaction"
          :topic-id="topicId"
          @changed="onReactionChanged"
      />
    </ul>

    <div v-if="!loading && hasMore" class="reactions__load-more">
      <button
          type="button"
          class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2 mx-auto"
          :disabled="loadingMore"
          @click="loadMore"
      >
        <span v-if="loadingMore">
          <span class="spinner-border spinner-border-sm" role="status"></span>
          Laden...
        </span>
        <span v-else>
          <i class="fa fa-ellipsis-h"></i>
          Meer laden ({{ remainingReactions }} resterend)
        </span>
      </button>
    </div>
  </div>
</template>